import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import chatService from '../services/chatService';
import { chatStorage } from '../utils/chatStorage';

export function useChat(courseId) {
  const { user } = useAuth();
  // Usa ID o email come identificatore univoco
  const userId = user?.id || user?.email;
  // console.log('useChat init:', { courseId, user, userId });

  const [messages, setMessages] = useState([]);
  const [modes, setModes] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [activeMode, setActiveModeState] = useState(null);
  const [activeFunctions, setActiveFunctionsState] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Carica dati iniziali
  useEffect(() => {
    if (!courseId) return;

    // Carica storico
    // console.log('useChat loading history for:', { courseId, userId });
    const history = chatStorage.getHistory(courseId, userId);
    setMessages(history);

    // Carica modalità salvata
    const savedMode = chatStorage.getActiveMode(courseId, userId);
    setActiveModeState(savedMode);

    // Carica funzioni salvate
    const savedFunctions = chatStorage.getActiveFunctions(courseId, userId);
    setActiveFunctionsState(savedFunctions);

    // Carica modalità e funzioni disponibili
    const loadConfiguration = async () => {
      try {
        const [modesData, functionsData] = await Promise.all([
          chatService.getModes(),
          chatService.getFunctions(),
        ]);
        setModes(modesData);
        setFunctions(functionsData);
      } catch (err) {
        console.error('Errore caricamento configurazione:', err);
      }
    };

    loadConfiguration();
  }, [courseId, userId]);

  // Invia messaggio
  const sendMessage = useCallback(async (messageText) => {
    if (!messageText.trim()) return;

    setError(null);
    setIsLoading(true);

    // Aggiungi messaggio utente
    const userMessage = {
      role: 'user',
      content: messageText,
      timestamp: new Date().toISOString(),
    };

    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    // console.log('useChat saving message for:', { courseId, userId });
    chatStorage.saveMessage(courseId, userId, userMessage);

    try {
      // Prepara storico per API (solo role e content, escludi l'ultimo)
      const history = messages.map((msg) => ({
        role: msg.role,
        content: msg.content,
      }));

      // Invia a backend
      const response = await chatService.sendMessage(
        courseId,
        messageText,
        history, // Storico senza l'ultimo messaggio appena aggiunto
        activeMode,
        activeFunctions
      );

      // Aggiungi risposta AI
      const aiMessage = response.message;
      setMessages((prev) => [...prev, aiMessage]);
      chatStorage.saveMessage(courseId, userId, aiMessage);

      return response;
    } catch (err) {
      const errorMessage = err.message || 'Errore durante l\'invio del messaggio';
      setError(errorMessage);
      console.error('Errore:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [courseId, messages, activeMode, activeFunctions, userId]);

  // Cambia modalità
  const setActiveMode = useCallback((modeId) => {
    setActiveModeState(modeId);
    chatStorage.saveActiveMode(courseId, userId, modeId);
  }, [courseId, userId]);

  // Toggle funzione
  const toggleFunction = useCallback((functionId) => {
    setActiveFunctionsState((prev) => {
      const newFunctions = prev.includes(functionId)
        ? prev.filter((id) => id !== functionId)
        : [...prev, functionId];
      chatStorage.saveActiveFunctions(courseId, userId, newFunctions);
      return newFunctions;
    });
  }, [courseId, userId]);

  // Pulisci chat
  const clearChat = useCallback(() => {
    setMessages([]);
    chatStorage.clearHistory(courseId, userId);
  }, [courseId, userId]);

  // Aggiunge un messaggio locale (senza inviarlo al backend)
  const addLocalMessage = useCallback((role, content) => {
    const newMessage = {
      role: role, // 'user' or 'ai' (or 'assistant')
      content: content,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, newMessage]);
    chatStorage.saveMessage(courseId, userId, newMessage);
  }, [courseId, userId]);

  return {
    messages,
    modes,
    functions,
    activeMode,
    activeFunctions,
    isLoading,
    error,
    sendMessage,
    setActiveMode,
    toggleFunction,
    clearChat,
    addLocalMessage, // Exposed helper
  };
}

